// ============================================================
// CLUBHUB – Kalender: Tage und Uhrzeiten
// ============================================================
// Uebernommen aus dem Agent (`lib/kalender/tag.ts`): der Kalender
// rechnet mit TAGEN als Text `YYYY-MM-DD` und ausschliesslich in
// UTC — wer mit `getDay()` rechnet, rechnet in der Zeitzone der
// Maschine, und Server (UTC) und Browser (Berlin) liegen an einem
// Sonntagabend in verschiedenen Wochen.
//
// Dazu kommen Zeitpunkte als „schwebende“ Ortszeit des Vereins
// (`2026-10-27T19:00:00`, ohne Zone). Gerechnet wird mit ihnen,
// indem man sie als UTC liest (`naivZuUtc`) — so rechnet auch
// `rrule` mit DTSTART ohne TZID. Die Uhrzeit wird nie umgerechnet:
// ein Training um 19:00 steht ueberall um 19:00.
// ============================================================

/** Ein Kalendertag, `YYYY-MM-DD`. */
export type DayKey = string;

const DAY_MS = 24 * 60 * 60 * 1000;

export const MONATE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
] as const;

export const MONATE_KURZ = ["Jan.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sept.", "Okt.", "Nov.", "Dez."] as const;

/** Montag zuerst. */
export const WOCHENTAGE_KURZ = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
export const WOCHENTAGE = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"] as const;

const TAG_MUSTER = /^\d{4}-\d{2}-\d{2}$/;

export function istTag(value: unknown): value is DayKey {
  return typeof value === "string" && TAG_MUSTER.test(value) && !Number.isNaN(Date.parse(value));
}

export function dateFromDay(day: DayKey): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!));
}

export function dayFromDate(date: Date): DayKey {
  return date.toISOString().slice(0, 10);
}

export function addDays(day: DayKey, n: number): DayKey {
  return dayFromDate(new Date(dateFromDay(day).getTime() + n * DAY_MS));
}

export function daysBetween(from: DayKey, to: DayKey): number {
  return Math.round((dateFromDay(to).getTime() - dateFromDay(from).getTime()) / DAY_MS);
}

/** 0 = Montag … 6 = Sonntag. */
export function weekdayIndex(day: DayKey): number {
  return (dateFromDay(day).getUTCDay() + 6) % 7;
}

export function isWeekend(day: DayKey): boolean {
  return weekdayIndex(day) >= 5;
}

export function mondayOf(day: DayKey): DayKey {
  return addDays(day, -weekdayIndex(day));
}

export function startOfMonth(day: DayKey): DayKey {
  return `${day.slice(0, 7)}-01`;
}

export function addMonths(day: DayKey, n: number): DayKey {
  const d = dateFromDay(startOfMonth(day));
  return dayFromDate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1)));
}

export function endOfMonth(day: DayKey): DayKey {
  return addDays(addMonths(day, 1), -1);
}

/** Kalenderwoche nach ISO 8601. */
export function isoWoche(day: DayKey): number {
  const donnerstag = addDays(mondayOf(day), 3);
  const jahresanfang = `${donnerstag.slice(0, 4)}-01-01`;
  return Math.floor(daysBetween(jahresanfang, donnerstag) / 7) + 1;
}

// ── Zeitpunkte (schwebende Ortszeit) ─────────────────────────

/** "2026-10-27T19:00:00" als UTC-Zeitpunkt — nur zum Rechnen. */
export function naivZuUtc(iso: string): Date {
  const [d, t = "00:00:00"] = iso.split("T");
  const [y, m, day] = d!.split("-").map(Number);
  const [hh = 0, mm = 0, ss = 0] = t.split(":").map(Number);
  return new Date(Date.UTC(y!, m! - 1, day!, hh, mm, ss));
}

export function utcZuNaiv(date: Date): string {
  return date.toISOString().slice(0, 19);
}

export function tagVon(iso: string): DayKey {
  return iso.slice(0, 10);
}

/** "19:00" */
export function uhrzeit(iso: string): string {
  return iso.slice(11, 16);
}

/** Minuten seit Mitternacht. */
export function minutenVon(iso: string): number {
  return Number(iso.slice(11, 13)) * 60 + Number(iso.slice(14, 16));
}

export function minutenZwischen(von: string, bis: string): number {
  return Math.round((naivZuUtc(bis).getTime() - naivZuUtc(von).getTime()) / 60000);
}

export function plusMinuten(iso: string, minuten: number): string {
  return utcZuNaiv(new Date(naivZuUtc(iso).getTime() + minuten * 60000));
}

/** Tag und Uhrzeit zu einem Zeitpunkt: ("2026-10-27", "19:00") → "2026-10-27T19:00:00". */
export function zeitpunkt(day: DayKey, hhmm: string): string {
  return `${day}T${hhmm.length === 5 ? `${hhmm}:00` : hhmm}`;
}

const BERLIN_TAG = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" });
const BERLIN_ZEIT = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Berlin", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });

/** Heute in der Zeitzone des Vereins (Server laeuft auf UTC). */
export function heuteImVerein(now: Date = new Date()): DayKey {
  return BERLIN_TAG.format(now);
}

/** Jetzt als schwebende Ortszeit des Vereins. */
export function jetztImVerein(now: Date = new Date()): string {
  return `${BERLIN_TAG.format(now)}T${BERLIN_ZEIT.format(now)}`;
}

// ── Schreibweisen ────────────────────────────────────────────

/** "27.10." */
export function formatTagKurz(day: DayKey): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}.`;
}

/** "27.10.2026" */
export function formatTagMitJahr(day: DayKey): string {
  return `${formatTagKurz(day)}${day.slice(0, 4)}`;
}

/** "Di 27.10." */
export function formatTagMitWochentag(day: DayKey): string {
  return `${WOCHENTAGE_KURZ[weekdayIndex(day)]} ${formatTagKurz(day)}`;
}

/** "Dienstag, 27. Oktober 2026" */
export function formatTagLang(day: DayKey, mitJahr = true): string {
  const monat = MONATE[Number(day.slice(5, 7)) - 1];
  return `${WOCHENTAGE[weekdayIndex(day)]}, ${Number(day.slice(8, 10))}. ${monat}${mitJahr ? ` ${day.slice(0, 4)}` : ""}`;
}

/** "Oktober 2026" */
export function formatMonat(day: DayKey): string {
  return `${MONATE[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}`;
}

/** „06.–08.10.“, „28.09.–04.10.“, „30.12.2026–02.01.2027“ */
export function formatZeitraum(from: DayKey, to: DayKey): string {
  if (from === to) return formatTagKurz(from);
  if (from.slice(0, 4) !== to.slice(0, 4)) return `${formatTagMitJahr(from)}–${formatTagMitJahr(to)}`;
  if (from.slice(0, 7) === to.slice(0, 7)) return `${from.slice(8, 10)}.–${formatTagKurz(to)}`;
  return `${formatTagKurz(from)}–${formatTagKurz(to)}`;
}

/**
 * Die Zeit eines Termins in einer Zeile: „19:00–20:30“, „ganztägig“,
 * „Sa 28.11., 15:00 – So 29.11., 00:30“ (ueber Mitternacht mit Tag).
 */
export function formatUhrzeiten(start: string, ende: string | null, ganztaegig: boolean): string {
  if (ganztaegig) {
    if (!ende || tagVon(ende) === tagVon(start)) return "ganztägig";
    return `ganztägig bis ${formatTagMitWochentag(tagVon(ende))}`;
  }
  if (!ende) return uhrzeit(start);
  if (tagVon(ende) !== tagVon(start)) return `${uhrzeit(start)} – ${formatTagMitWochentag(tagVon(ende))}, ${uhrzeit(ende)}`;
  return `${uhrzeit(start)}–${uhrzeit(ende)}`;
}

/** „Dienstag, 27. Oktober 2026 · 19:00–20:30 Uhr“ */
export function formatTerminZeit(start: string, ende: string | null, ganztaegig: boolean): string {
  const tag = formatTagLang(tagVon(start));
  if (ganztaegig) {
    if (ende && tagVon(ende) !== tagVon(start)) return `${formatTagMitWochentag(tagVon(start))} bis ${formatTagLang(tagVon(ende))} · ganztägig`;
    return `${tag} · ganztägig`;
  }
  const zeit = formatUhrzeiten(start, ende, false);
  return `${tag} · ${zeit} Uhr`;
}

/** „in 3 Tagen“, „morgen“, „heute“, „vor 2 Tagen“ — fuer Fristen. */
export function relativTage(day: DayKey, heute: DayKey): string {
  const n = daysBetween(heute, day);
  if (n === 0) return "heute";
  if (n === 1) return "morgen";
  if (n === -1) return "gestern";
  if (n > 1) return `in ${n} Tagen`;
  return `vor ${-n} Tagen`;
}

// ── Gespeicherte Zeitpunkte (Datenbank) ──────────────────────
// In der Datenbank steht der ECHTE Zeitpunkt (UTC), in den Masken
// die schwebende Ortszeit des Vereins. Umgerechnet wird nur an der
// Grenze (Lesen und Schreiben), immer ueber Europe/Berlin — so steht
// ein Training um 19:00 auch nach der Zeitumstellung um 19:00.

/** Wie weit die Ortszeit des Vereins zu einem Zeitpunkt vor UTC liegt (ms). */
function versatzBerlin(d: Date): number {
  const ganz = Math.floor(d.getTime() / 1000) * 1000;
  return naivZuUtc(jetztImVerein(new Date(ganz))).getTime() - ganz;
}

/** "2026-10-27T19:00:00" (Ortszeit Berlin) als echter Zeitpunkt. */
export function berlinZuUtc(naiv: string): Date {
  const als = naivZuUtc(naiv).getTime();
  // Zweimal, weil der Versatz am Umstellungstag vom Ergebnis abhaengt.
  const erst = als - versatzBerlin(new Date(als));
  return new Date(als - versatzBerlin(new Date(erst)));
}

/** Ein gespeicherter Zeitpunkt als schwebende Ortszeit des Vereins. */
export function utcZuBerlin(d: Date): string {
  return jetztImVerein(d);
}

/** Ist das eine schwebende Ortszeit `YYYY-MM-DDTHH:MM(:SS)`? */
export function istZeitpunkt(value: unknown): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value) && istTag(value.slice(0, 10));
}

/** Auf Sekunden ergaenzt: "2026-10-27T19:00" → "2026-10-27T19:00:00". */
export function vollerZeitpunkt(iso: string): string {
  return iso.length === 16 ? `${iso}:00` : iso.slice(0, 19);
}
